/* eslint-disable react-hooks/exhaustive-deps */
import { useAuthenticatedUserQuery } from "@/api/queries/AuthQueries";
import LoginForm from "@/components/forms/auth/LoginForm";
import OAuthForm from "@/components/forms/auth/OAuthForm";
import { TotpVerifyForm } from "@/components/forms/auth/TotpVerifyForm";
import { TotpSetupForm } from "@/components/forms/auth/TotpSetupForm";
import { BackupCodesDisplay } from "@/components/forms/auth/BackupCodesDisplay";
import { useLoginMutation } from "@/api/mutations/AuthMutations";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { UseTheme } from "@/components/ThemeProvider";
import { useTranslation } from "react-i18next";

type LoginStep = "LOGIN" | "TOTP_VERIFY" | "TOTP_SETUP" | "BACKUP_CODES";

const Login = () => {
    const { t } = useTranslation();
    const [loginError, setLoginError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentStep, setCurrentStep] = useState<LoginStep>("LOGIN");
    const [setupPassword, setSetupPassword] = useState("");
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const { theme, setTheme } = UseTheme();
    const originalTheme = useRef(theme);

    useEffect(() => {
        if (theme !== "light") {
            setTheme("light");
        }

        return () => {
            setTheme(originalTheme.current);
        };
    }, []);
    const navigate = useNavigate();

    const loginMutation = useLoginMutation(setIsLoading, (error) => {
        // Check if this is a 2FA redirect
        if (error.includes("twoFactorRedirect") || error.includes("2FA")) {
            setCurrentStep("TOTP_VERIFY");
        } else {
            setLoginError(error);
        }
    });

    const { data } = useAuthenticatedUserQuery();

    if (data?.user) {
        return <Navigate to="/" />;
    }

    const handleLogin = (credentials: { email: string; password: string }) => {
        setSetupPassword(credentials.password); // Store for potential TOTP setup
        loginMutation.mutate(credentials);
    };

    const handleTotpSetupComplete = (codes: string[]) => {
        setBackupCodes(codes);
        setCurrentStep("BACKUP_CODES");
    };

    const handleBackupCodesComplete = () => {
        navigate("/");
    };

    const handleBackToLogin = () => {
        setCurrentStep("LOGIN");
        setLoginError(null);
        setSetupPassword("");
    };
    return (
        <div
            style={{
                margin: 0,
                padding: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
            }}
        >
            <div className="container relative h-screen flex flex-col lg:flex-row items-center justify-center lg:px-0">
                <div className="relative hidden flex-1 h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
                    <div
                        className="absolute inset-0"
                        style={{
                            background: `linear-gradient(135deg, #6366f1, #ec4899), radial-gradient(circle, rgba(255,255,255,0.1), transparent 100%)`,
                            backgroundBlendMode: "overlay",
                        }}
                    />
                    <div className="relative z-20 flex items-center text-3xl font-semibold">Junet.io</div>
                    <div className="relative z-20 mt-auto">
                        <blockquote className="space-y-2">
                            <p className="text-lg">{t("login.aiMadeSimple")}</p>
                            <footer className="text-sm">
                                {t("login.description")}
                            </footer>
                        </blockquote>
                    </div>
                </div>
                <div className="lg:p-8 flex-1 flex items-center">
                    <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
                        <div className="flex flex-col space-y-2 text-center">
                            <h1 className="text-2xl font-semibold tracking-tight">{t("login.welcomeBack")}</h1>
                            <p className="text-sm text-muted-foreground">{t("login.enterEmail")}</p>
                        </div>
                        <div className="flex flex-col gap-3">
                            {currentStep === "LOGIN" && (
                                <>
                                    <OAuthForm />
                                    <LoginForm
                                        onSubmit={handleLogin}
                                        loading={isLoading}
                                        error={loginError || undefined}
                                    />
                                </>
                            )}

                            {currentStep === "TOTP_VERIFY" && (
                                <TotpVerifyForm
                                    onSuccess={() => navigate("/")}
                                    onCancel={handleBackToLogin}
                                    callbackURL="/"
                                />
                            )}

                            {currentStep === "TOTP_SETUP" && (
                                <TotpSetupForm
                                    password={setupPassword}
                                    onSetupComplete={handleTotpSetupComplete}
                                    onCancel={handleBackToLogin}
                                />
                            )}

                            {currentStep === "BACKUP_CODES" && (
                                <BackupCodesDisplay backupCodes={backupCodes} onContinue={handleBackupCodesComplete} />
                            )}
                        </div>
                        <p className="px-8 text-center text-sm text-muted-foreground">
                            {t("login.byClickingContinue")}{" "}
                            <Link
                                to="https://junet.io/terms"
                                className="underline underline-offset-4 hover:text-primary"
                                target="_blank"
                            >
                                {t("login.termsOfService")}
                            </Link>{" "}
                            and{" "}
                            <Link
                                to="https://junet.io/privacy"
                                className="underline underline-offset-4 hover:text-primary"
                                target="_blank"
                            >
                                {t("login.privacyPolicy")}
                            </Link>
                            .
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
